import React, { useState, useMemo } from 'react';
import './StatusTable.css';
import { formatDateTime } from '../utils/dateFormat';

const StatusTable = ({ sites, onSelectSite }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-icon status-active">●</span>;
      case 'warning':
        return <span className="status-icon status-warning">⚠</span>;
      case 'critical':
        return <span className="status-icon status-critical">!</span>;
      case 'not-monitored':
        return <span className="status-icon status-not-monitored">○</span>;
      default:
        return <span className="status-icon status-unknown">?</span>;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'warning':
        return 'Warning';
      case 'critical':
        return 'Critical';
      case 'not-monitored':
        return 'Not Monitored';
      default:
        return 'Unknown';
    }
  };


  // Get timestamp value for sorting
  const getTimestampValue = (timestamp) => {
    if (!timestamp) return 0;
    return new Date(timestamp).getTime();
  };

  // Status priority for sorting (lower number = higher priority)
  const getStatusPriority = (status) => {
    switch (status) {
      case 'critical': return 1;
      case 'warning': return 2;
      case 'active': return 3;
      case 'not-monitored': return 4;
      default: return 5;
    }
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort sites based on current sort column and direction
  // Default to sorting by status if no column is selected
  const sortedSites = useMemo(() => {
    if (!sortColumn) {
      // Default sort by status priority (Critical, Warning, Active, Not Monitored)
      return [...sites].sort((a, b) => {
        const priorityA = getStatusPriority(a.status);
        const priorityB = getStatusPriority(b.status);
        if (priorityA !== priorityB) {
          return priorityA - priorityB; // Lower priority number = higher in list
        }
        // If same status, sort by site ID as secondary sort
        return a.id - b.id;
      });
    }

    const sorted = [...sites].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'ip':
          aValue = (a.ip || '').toLowerCase();
          bValue = (b.ip || '').toLowerCase();
          break;
        case 'status':
          aValue = getStatusPriority(a.status);
          bValue = getStatusPriority(b.status);
          break;
        case 'timestamp':
          aValue = getTimestampValue(a.timestamp);
          bValue = getTimestampValue(b.timestamp);
          break;
        case 'myParkVersion':
          aValue = (a.myParkVersion || '').toLowerCase();
          bValue = (b.myParkVersion || '').toLowerCase();
          break;
        case 'agentVersion':
          aValue = (a.agentVersion || '1.0.0').toLowerCase();
          bValue = (b.agentVersion || '1.0.0').toLowerCase();
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [sites, sortColumn, sortDirection]);

  // Get sort indicator icon
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <span className="sort-icon">⇅</span>;
    }
    return sortDirection === 'asc' ? <span className="sort-icon sort-asc">↑</span> : <span className="sort-icon sort-desc">↓</span>;
  };

  return (
    <div className="status-table-container">
      <div className="status-table-wrapper">
        <table className="status-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort('id')}>
              Site ID {getSortIcon('id')}
            </th>
            <th className="sortable" onClick={() => handleSort('name')}>
              Site Name {getSortIcon('name')}
            </th>
            <th className="sortable" onClick={() => handleSort('ip')}>
              IP Address {getSortIcon('ip')}
            </th>
            <th className="sortable" onClick={() => handleSort('status')}>
              Status {getSortIcon('status')}
            </th>
            <th className="sortable" onClick={() => handleSort('timestamp')}>
              Last Update {getSortIcon('timestamp')}
            </th>
            <th className="sortable" onClick={() => handleSort('myParkVersion')}>
              MyPark Version {getSortIcon('myParkVersion')}
            </th>
            <th className="sortable" onClick={() => handleSort('agentVersion')}>
              Agent Version {getSortIcon('agentVersion')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedSites.length === 0 ? (
            <tr>
              <td colSpan="7" className="no-data">No sites found</td>
            </tr>
          ) : (
            sortedSites.map((site) => (
              <tr
                key={site.id}
                className={`table-row ${site.status}`}
                onClick={() => onSelectSite(site)}
                style={{ cursor: 'pointer' }}
              >
                <td className="site-id">{site.id}</td>
                <td className="site-name">{site.name}</td>
                <td className="site-ip">
                  <span 
                    style={{
                      color: site.ip && site.publicIp 
                        ? (site.ip.trim() === site.publicIp.trim() ? '#10b981' : '#fbbf24')
                        : 'inherit'
                    }}
                  >
                    {site.ip}
                  </span>
                </td>
                <td className="site-status">
                  {getStatusIcon(site.status)}
                  <span>{getStatusText(site.status)}</span>
                </td>
                <td className="site-update">
                  {formatDateTime(site.timestamp)}
                </td>
                <td className="site-version">
                  {site.myParkVersion || 'N/A'}
                </td>
                <td className="site-version">
                  {site.heartbeat
                    ? (site.agentVersion || '1.0.0')
                    : 'N/A'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default StatusTable;

